import React from "react";
import { Gift, Sparkles } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const SCREENS = [
  {
    src: ASSETS.fifiRewards,
    title: "Daily Missions",
    desc: "Check-in, learn, save. Earn XP every day.",
    chipBg: "bg-[#EDE9FE]",
    chipText: "text-[#5B21B6]",
    rotate: "-3deg",
  },
  {
    src: ASSETS.fifiCloset,
    title: "Fifi Closet",
    desc: "Dress Fifi up. Mix outfits, shoes & accessories.",
    chipBg: "bg-[#FCE7F3]",
    chipText: "text-[#9D174D]",
    rotate: "2deg",
  },
  {
    src: ASSETS.fifiStoreScreen,
    title: "Fifi Store",
    desc: "Tier 1–6 drops. From hoodies to a literal jet.",
    chipBg: "bg-[#DBEAFE]",
    chipText: "text-[#1E40AF]",
    rotate: "-2deg",
  },
  {
    src: ASSETS.fifiWorld,
    title: "Fifi World",
    desc: "Build wealth, upgrade Fifi's room & lifestyle.",
    chipBg: "bg-[#FFEDD5]",
    chipText: "text-[#9A3412]",
    rotate: "3deg",
  },
];

const PhoneFrame = ({ src, alt }) => (
  <div className="relative rounded-[2.4rem] bg-slate-900 p-2 shadow-2xl">
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-900 rounded-full z-20" />
    <div className="rounded-[2rem] overflow-hidden bg-white aspect-[9/19.5]">
      <img src={src} alt={alt} className="w-full h-full object-cover object-top" />
    </div>
  </div>
);

const FifiStore = () => {
  return (
    <section
      id="fifi-store"
      data-testid="fifi-store-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -right-32 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#FCE7F3] to-[#EDE9FE] blur-3xl opacity-60" />
        <div className="absolute bottom-20 -left-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#DBEAFE] to-[#FFEDD5] blur-3xl opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#EDE9FE] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#5B21B6]">
            <Gift size={14} /> Fifi Rewards System
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            Smart money habits unlock{" "}
            <span className="brand-gradient-text">Fifi's world.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Daily missions → XP → outfits, rooms, garages, yachts, and a private jet.
            The more you learn, the bigger Fifi's world grows.
          </p>
        </div>

        {/* 4 phone mockups */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {SCREENS.map((s) => (
            <div
              key={s.title}
              data-testid={`fifi-store-screen-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex flex-col"
            >
              <div
                className="transition-transform duration-500 group-hover:-translate-y-3 group-hover:rotate-0"
                style={{ transform: `rotate(${s.rotate})` }}
              >
                <PhoneFrame src={s.src} alt={s.title} />
              </div>
              <div className="mt-7 text-center">
                <span
                  className={`inline-flex items-center gap-1 rounded-full ${s.chipBg} ${s.chipText} px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]`}
                >
                  <Sparkles size={11} /> {s.title}
                </span>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed max-w-[220px] mx-auto">
                  {s.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Fifi's Journey — official 6-tier progression */}
        <div className="mt-20 rounded-3xl bg-white border border-slate-100 p-6 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
          <div className="text-center mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              The 6-tier journey
            </div>
            <h3 className="font-display mt-2 text-2xl md:text-3xl font-black text-slate-900">
              From <span className="brand-gradient-text">Rookie Cat</span> to{" "}
              <span className="brand-gradient-text">FinX Legend.</span>
            </h3>
            <p className="mt-3 text-sm md:text-base text-slate-500 max-w-2xl mx-auto">
              Learn · Save · Analyze · Strategize · Build · Achieve — every XP brings Fifi
              one step closer to financial freedom.
            </p>
          </div>
          <img
            src="/assets/fifi/journey.png"
            alt="Fifi's Journey — 6 tier progression from Rookie Cat to FinX Legend"
            data-testid="fifi-journey-image"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
};

export default FifiStore;
